import { useState, useRef } from 'react'
import useSignupDraft from '../../hooks/useSignupDraft.jsx'
import shrinkImage from '../../utils/shrinkImage'
import { useNavigate, Link } from 'react-router-dom'
import { Loader2, CheckCircle2, Eye, EyeOff, Upload, FileText, Sparkles } from 'lucide-react'
import publicApi from '../../services/publicApi'
import InstallAppButton from '../common/InstallAppButton.jsx'
import ShareTabNote from '../common/ShareTabNote.jsx'
import { useTranslation } from 'react-i18next'

/**
 * Public self-signup — identical onboarding experience to the Admin-invite page
 * (CV auto-fill, KYC ID extraction, profile pic + certifications, bookmark+redirect).
 * The only difference: the user picks their own role (no Admin self-signup).
 * Posts a multipart form to /auth/register/.
 */
// Funder is paused for now — sign-up is limited to these roles.
const ROLES = ['Consultant', 'Project Manager', 'Finance']
// Mirrors the backend's is_valid_linkedin_url() exactly — a real profile URL, not just
// any linkedin.com link (e.g. a company page) or arbitrary text.
const LINKEDIN_URL_RE = /^https?:\/\/([a-z]{2,3}\.)?linkedin\.com\/in\/[A-Za-z0-9\-_%]+\/?(\?.*)?$/i

// Loose token-overlap check, not exact match — "Dhiraj Mattur" vs "Dhiraj K. Mattur" or
// minor formatting differences shouldn't false-alarm. Flags only a genuinely different
// name (e.g. the CV extracted "Robert Chen" for a form that already said "Dhiraj Mattur").
function namesLikelyDiffer(typedName, cvName) {
  const norm = s => (s || '').toLowerCase().replace(/[^a-z\s]/g, '').split(/\s+/).filter(Boolean)
  const typed = norm(typedName)
  const extracted = norm(cvName)
  if (!typed.length || !extracted.length) return false
  const overlap = typed.filter(t => extracted.includes(t)).length
  return overlap === 0
}

export default function Register() {
  const { t: tch } = useTranslation('chrome')
  const navigate = useNavigate()

  const [role, setRole] = useState('Consultant')
  const isConsultant = role === 'Consultant'
  const [form, setForm] = useState({
    full_name: '', email: '', password: '', confirm_password: '',
    phone_number: '', linkedin_url: '', specialization: '', organization_name: '',
  })
  const [showPwd, setShowPwd] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  // CV upload + AI auto-fill
  const cvFileRef = useRef(null)
  // A second, always-mounted file input for the "fill manually" section below — the AI
  // dropzone's own input unmounts once cvAutofilled is true, so a button down here can't
  // reuse cvFileRef (it'd silently do nothing after a successful autofill).
  const manualCvFileRef = useRef(null)
  const [cvFile, setCvFile] = useState(null)
  const [cvParsing, setCvParsing] = useState(false)
  const [cvError, setCvError] = useState(null)
  const [cvAutofilled, setCvAutofilled] = useState(false)
  const [cvNameMismatch, setCvNameMismatch] = useState(null) // the CV's extracted name, if it looks like someone else's

  // KYC ID
  const idFileRef = useRef(null)
  const [idType, setIdType] = useState('Passport')
  const [idNumber, setIdNumber] = useState('')
  const [idFile, setIdFile] = useState(null)
  const [idParsing, setIdParsing] = useState(false)
  const [idError, setIdError] = useState(null)
  // null when the reader was not used at all, so nothing is claimed either way.
  const [idVerified, setIdVerified] = useState(null)
  // Also read off the ID and passed through to the backend, which seeds them into the KYC
  // identity step. Not shown as form fields here — the consultant checks and edits them there.
  const [idNationality, setIdNationality] = useState('')
  const [idDob, setIdDob] = useState('')
  const [idAddress, setIdAddress] = useState('')

  // Profile pic + certs
  const profileRef = useRef(null)
  const certRef = useRef(null)
  const [profileFile, setProfileFile] = useState(null)
  const [certFile, setCertFile] = useState(null)

  // ── SURVIVING A REFRESH ───────────────────────────────────────────
  // Nothing on this form has reached the server yet: the CV, the ID photo, the profile picture
  // and every typed field exist only in this tab until submit. One reload on a weak connection
  // cost a consultant twenty minutes, and there was nothing to come back to.
  //
  // Now the text is written to this browser and the files to IndexedDB, which can hold the
  // bytes. Nothing goes over the network to do it -- on a connection that is failing, that is
  // the whole point. Passwords are never written.
  const draft = useSignupDraft('open-signup', {
    role, form, idType, idNumber, idNationality, idDob, cvAutofilled,
    cvFile, idFile, profileFile, certFile,
  }, { omit: ['password', 'confirm_password'] })

  const [restoredNote, setRestoredNote] = useState(null)

  const restoreDraft = () => {
    const d = draft.restored
    if (!d) return
    if (d.role) setRole(d.role)
    if (d.form) setForm(f => ({ ...f, ...d.form, password: f.password, confirm_password: f.confirm_password }))
    if (d.idType) setIdType(d.idType)
    if (d.idNumber) setIdNumber(d.idNumber)
    if (d.idNationality) setIdNationality(d.idNationality)
    if (d.idDob) setIdDob(d.idDob)
    if (d.cvAutofilled) setCvAutofilled(true)
    if (d.cvFile) setCvFile(d.cvFile)
    if (d.idFile) setIdFile(d.idFile)
    if (d.profileFile) setProfileFile(d.profileFile)
    if (d.certFile) setCertFile(d.certFile)
    const gotFiles = [d.cvFile, d.idFile, d.profileFile, d.certFile].filter(Boolean).length
    setRestoredNote(
      gotFiles
        ? `Your details are back, along with ${gotFiles} file${gotFiles > 1 ? 's' : ''} you had attached. Please set a password again — that is the one thing we never keep.`
        : 'Your details are back. Please set a password again — that is the one thing we never keep.'
    )
    draft.dismiss()
  }

  const set = (f, v) => setForm(prev => ({ ...prev, [f]: v }))

  // Somebody whose company signs in through its own system should never fill this form in.
  // Their account is created for them the first time they arrive; a second one made here would
  // hold a password and a role they chose themselves, and their employer could only switch off
  // one of the two. The server refuses it either way — this is so they find out at the email
  // box rather than after typing everything and uploading a CV.
  const [managedOrg, setManagedOrg] = useState(null)

  const checkDoor = async () => {
    const email = (form.email || '').trim()
    if (!email.includes('@')) { setManagedOrg(null); return }
    try {
      const { data } = await publicApi.post('/auth/identify', { email })
      setManagedOrg(data?.managed && data.kind === 'organisation' ? data : null)
    } catch {
      setManagedOrg(null)   // a failed lookup must never block a legitimate sign-up
    }
  }

  const handleCvUpload = async (file) => {
    if (!file) return
    if (!/\.(pdf|docx)$/i.test(file.name || '')) { setCvError('Please upload a PDF or DOCX file.'); return }
    if (file.size > 5 * 1024 * 1024) { setCvError('File too large (max 5 MB).'); return }
    // The file itself is attached regardless of whether AI parsing succeeds — parsing is
    // just a convenience autofill; a scanned/image-only PDF, a rate limit, or the AI
    // service being down must never mean "your CV wasn't attached."
    setCvFile(file)
    setCvError(null); setCvNameMismatch(null); setCvParsing(true)
    try {
      const fd = new FormData(); fd.append('file', file)
      const { data } = await publicApi.post('/auth/parse-cv', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      // Compare against whatever name was ALREADY typed, before the autofill below
      // overwrites it — otherwise the comparison would trivially always match.
      if (data.full_name && form.full_name.trim() && namesLikelyDiffer(form.full_name, data.full_name)) {
        setCvNameMismatch(data.full_name)
      }
      setForm(prev => ({
        ...prev,
        full_name: data.full_name || prev.full_name,
        email: data.email || prev.email,
        phone_number: data.phone_number || prev.phone_number,
        linkedin_url: data.linkedin_url || prev.linkedin_url,
        specialization: data.specialization || prev.specialization,
      }))
      setCvAutofilled(true)
    } catch (e) {
      setCvError(e.response?.data?.detail || 'Could not read this CV automatically — your file is still attached; please fill the fields manually.')
    } finally { setCvParsing(false) }
  }

  const handleIdUpload = async (file) => {
    if (!file) return
    if (!/\.(pdf|png|jpe?g|webp)$/i.test(file.name || '')) { setIdError('Upload an image (PNG/JPG) or PDF.'); return }
    // Shrink BEFORE the size check: a phone photo of an ID is often over the limit as taken and
    // a few hundred kilobytes once re-encoded. Rejecting it first turned a file we can happily
    // accept into a dead end.
    file = await shrinkImage(file)
    if (file.size > 10 * 1024 * 1024) { setIdError('File too large (max 10 MB).'); return }
    setIdError(null); setIdVerified(null); setIdParsing(true)
    try {
      const fd = new FormData(); fd.append('file', file)
      const { data } = await publicApi.post('/auth/parse-id', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      if (data.id_type) setIdType(data.id_type)
      if (data.id_number) setIdNumber(data.id_number)
      if (data.full_name && !form.full_name) set('full_name', data.full_name)
      if (data.nationality) setIdNationality(data.nationality)
      if (data.date_of_birth) setIdDob(data.date_of_birth)
      if (data.address) setIdAddress(data.address)
      setIdFile(file)
      // WHETHER THE DOCUMENT PROVED ITS OWN READING, which is not the same as whether we read
      // something. A passport, an Aadhaar and an IBAN each carry a check digit, so a misreading
      // fails and what comes back is certain. A PAN, an Indian account number and anything read
      // off printed text carry nothing of the kind -- they look right, which is all. Saying so
      // is the difference between a number somebody glances at and one they actually check.
      setIdVerified(data.verified === undefined ? null : !!data.verified)
      if (!data.id_number) setIdError("Saved your ID but couldn't read the number — please type it.")
    } catch (e) {
      setIdError(e.response?.data?.detail || 'Could not read this ID. Try a clearer photo or PDF.')
    } finally { setIdParsing(false) }
  }

  const submit = async (e) => {
    e.preventDefault()
    setError(null)
    if (!form.full_name.trim() || !form.email.trim() || !form.password) { setError('Full name, email and password are required.'); return }
    if (form.password.length < 6) { setError('Password must be at least 6 characters.'); return }
    if (form.password !== form.confirm_password) { setError('Passwords do not match.'); return }
    // LinkedIn OR CV + phone (KYC) are required for Consultants only — either one is
    // enough verification, but whichever is provided must actually be valid. PM/Finance/
    // Funder just create a basic account. The backend enforces the same rule regardless;
    // this just gives a fast, clear error before the round-trip.
    if (isConsultant) {
      if (!form.phone_number.trim()) { setError('Phone number is required.'); return }
      const linkedinValid = LINKEDIN_URL_RE.test(form.linkedin_url.trim())
      if (form.linkedin_url.trim() && !linkedinValid) {
        setError('Please enter a valid LinkedIn profile URL (e.g. https://www.linkedin.com/in/your-name).')
        return
      }
      if (!linkedinValid && !cvFile) {
        setError('Please provide either your LinkedIn profile URL or upload your CV.')
        return
      }
    }
    setSubmitting(true)
    try {
      const fd = new FormData()
      fd.append('role', role)
      fd.append('full_name', form.full_name.trim())
      fd.append('email', form.email.trim().toLowerCase())
      fd.append('password', form.password)
      if (form.phone_number.trim()) fd.append('phone_number', form.phone_number.trim())
      if (form.linkedin_url.trim()) fd.append('linkedin_url', form.linkedin_url.trim())
      if (form.specialization.trim()) fd.append('specialization', form.specialization.trim())
      if (!isConsultant && form.organization_name.trim()) fd.append('organization_name', form.organization_name.trim())
      if (cvFile) fd.append('resume', cvFile)
      if (idFile) { fd.append('id_document', idFile); fd.append('id_document_type', idType) }
      if (idNumber.trim()) fd.append('id_document_number', idNumber.trim())
      if (idNationality.trim()) fd.append('id_nationality', idNationality.trim())
      if (idDob.trim()) fd.append('id_date_of_birth', idDob.trim())
      if (idAddress.trim()) fd.append('id_mailing_address', idAddress.trim())
      if (profileFile) fd.append('profile_picture', profileFile)
      if (certFile) fd.append('certifications_file', certFile)
      await publicApi.post('/auth/register/', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      draft.clear()   // the account exists now; nothing left to come back to
      setSuccess(true)
    } catch (e) {
      setError(e.response?.data?.detail || 'Registration failed. Please try again.')
    } finally { setSubmitting(false) }
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-green-50 flex items-center justify-center mb-3">
            <CheckCircle2 className="w-7 h-7 text-green-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">{tch('welcome_aboard')}</h1>
          <p className="text-sm text-gray-600 mb-5">
            Your account has been created. Install the platform on your desktop for quick access, then log in with the password you just set.
          </p>
          <InstallAppButton />
          <button onClick={() => navigate('/login')} className="w-full mt-2 text-sm text-gray-500 hover:text-gray-700">{tch('continue_to_login')}</button>
        </div>
      </div>
    )
  }

  const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
  const labelCls = "block text-xs font-medium text-gray-700 mb-1"

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-extrabold text-gray-900">
            Join <span className="text-transparent bg-clip-text bg-linear-to-r from-blue-600 to-indigo-600">ProTrack</span>
          </h1>
          <p className="mt-2 text-sm text-gray-600">{tch('create_account')}</p>
        </div>

        <form onSubmit={submit} autoComplete="off" className="bg-white rounded-2xl shadow-lg p-6 space-y-4 border border-gray-100">
          <input type="text" name="fake-username" autoComplete="username" tabIndex={-1} aria-hidden="true" style={{ position: 'absolute', left: '-9999px', height: 0, width: 0 }} />
          <input type="password" name="fake-password" autoComplete="current-password" tabIndex={-1} aria-hidden="true" style={{ position: 'absolute', left: '-9999px', height: 0, width: 0 }} />

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">{error}</div>}
          {/* Offered, not applied. Somebody who walked away from a half-filled form on purpose
              should not find it waiting; somebody who lost one should not have to start again. */}
          {draft.restored && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
              <p className="text-sm text-amber-900 font-medium">You had started signing up</p>
              <p className="text-xs text-amber-800 mt-0.5">
                What you typed and the files you attached were kept on this device
                {draft.savedAt ? ` (${new Date(draft.savedAt).toLocaleString()})` : ''}.
              </p>
              <div className="flex gap-3 mt-2">
                <button type="button" onClick={restoreDraft}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white">
                  Bring it back
                </button>
                <button type="button" onClick={draft.clear}
                  className="text-xs text-amber-800 hover:underline">
                  Start fresh
                </button>
              </div>
            </div>
          )}

          {restoredNote && (
            <div className="bg-green-50 border border-green-200 text-green-800 text-xs px-4 py-2.5 rounded-lg">
              {restoredNote}
            </div>
          )}

          {/* Role */}
          <div>
            <label className={labelCls}>I am registering as *</label>
            <select value={role} onChange={e => setRole(e.target.value)} className={inputCls}>
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          {/* CV upload — Quick Fill (Consultants only) */}
          {isConsultant && (
          <div className={`rounded-xl border-2 ${cvAutofilled ? 'border-green-300 bg-green-50' : cvFile ? 'border-amber-300 bg-amber-50' : 'border-dashed border-blue-300 bg-blue-50/60'} p-4 transition-colors`}>
            {!cvFile && !cvParsing && (
              <div onClick={() => cvFileRef.current?.click()} className="cursor-pointer text-center py-4">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-linear-to-r from-blue-500 to-indigo-600 mb-2">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <p className="text-sm font-semibold text-gray-800">{tch('upload_cv_autofill')}</p>
                <p className="text-xs text-gray-500 mt-0.5">{tch('click_upload_cv')}</p>
                <input ref={cvFileRef} type="file" accept=".pdf,.docx" className="hidden" onChange={e => e.target.files?.[0] && handleCvUpload(e.target.files[0])} />
              </div>
            )}
            {cvParsing && (
              <div className="text-center py-4"><Loader2 className="w-6 h-6 text-blue-600 animate-spin mx-auto mb-2" /><p className="text-sm font-medium text-gray-700">{tch('reading_cv')}</p></div>
            )}
            {cvAutofilled && !cvParsing && (
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-green-800">{tch('auto_filled_from')} <span className="font-mono">{cvFile?.name}</span></p>
                  <p className="text-xs text-green-700 mt-0.5">{tch('review_before_submit')}</p>
                </div>
                <button type="button" onClick={() => { setCvAutofilled(false); setCvFile(null); setCvNameMismatch(null) }} className="text-xs text-blue-700 hover:underline shrink-0">{tch('change_cv')}</button>
              </div>
            )}
            {cvFile && !cvAutofilled && !cvParsing && (
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-amber-800">{tch('cv_attached')} <span className="font-mono">{cvFile.name}</span></p>
                  <p className="text-xs text-amber-700 mt-0.5">Couldn't auto-read it — your file is still attached; fill the fields below manually.</p>
                </div>
                <button type="button" onClick={() => { setCvFile(null); setCvError(null); setCvNameMismatch(null) }} className="text-xs text-blue-700 hover:underline shrink-0">{tch('change_cv')}</button>
              </div>
            )}
            {cvError && <p className="text-xs text-red-600 mt-2">{cvError}</p>}
          </div>
          )}

          {isConsultant && (
          <div className="flex items-center gap-3 my-1">
            <div className="flex-1 h-px bg-gray-200" /><span className="text-xs text-gray-400 uppercase tracking-widest">or fill manually</span><div className="flex-1 h-px bg-gray-200" />
          </div>
          )}

          <ShareTabNote />

          <div>
            <label className={labelCls}>{tch('full_name_req')}</label>
            <input type="text" required value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="e.g. Jane Smith" className={inputCls} name="reg-fullname" autoComplete="off" />
          </div>
          <div>
            <label className={labelCls}>{tch('email_req')}</label>
            <input type="email" required value={form.email}
              onChange={e => { setManagedOrg(null); set('email', e.target.value) }}
              onBlur={checkDoor}
              placeholder="jane@example.com" className={inputCls} name="reg-email" autoComplete="off" data-form-type="other" />
            {managedOrg && (
              <div className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50/60 p-3">
                <p className="text-sm text-indigo-900">
                  <strong>{managedOrg.organisation_name}</strong> signs in through their own
                  system — you do not need to sign up. Your account is created the first time
                  you arrive.
                </p>
                <a href={managedOrg.signin_url}
                   className="mt-2 inline-flex w-full items-center justify-center rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700">
                  Continue with {managedOrg.organisation_name} →
                </a>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{tch('password_req')}</label>
              <div className="relative">
                <input type={showPwd ? 'text' : 'password'} data-private={showPwd || undefined} required value={form.password} onChange={e => set('password', e.target.value)} placeholder={tch('ph_min6')} className={`${inputCls} pr-9`} name="reg-new-password" autoComplete="new-password" />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">{showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div>
            </div>
            <div>
              <label className={labelCls}>{tch('confirm_password_req')}</label>
              <input type={showPwd ? 'text' : 'password'} data-private={showPwd || undefined} required value={form.confirm_password} onChange={e => set('confirm_password', e.target.value)} placeholder={tch('ph_retype_password')} className={inputCls} name="reg-confirm-password" autoComplete="new-password" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Phone Number {isConsultant && '*'}<span className="ml-1 text-[10px] font-normal text-gray-400">{isConsultant ? '(required)' : '(optional)'}</span></label>
            <input type="tel" data-private required={isConsultant} value={form.phone_number} onChange={e => set('phone_number', e.target.value)} placeholder="+971 50 123 4567" className={inputCls} name="reg-phone" autoComplete="off" />
          </div>

          {/* Project Managers and Finance: two optional lines about who they are. Both are stored
              already (the backend takes them for any role); the form only ever asked consultants. */}
          {!isConsultant && (<>
          <div>
            <label className={labelCls}>Organization <span className="ml-1 text-[10px] font-normal text-gray-400">(optional)</span></label>
            <input type="text" value={form.organization_name} onChange={e => set('organization_name', e.target.value)} placeholder="e.g. 3 Pillars Data & Training" className={inputCls} name="reg-organization" autoComplete="organization" />
          </div>
          <div>
            <label className={labelCls}>{tch('specialization')} <span className="ml-1 text-[10px] font-normal text-gray-400">(optional)</span></label>
            <input type="text" value={form.specialization} onChange={e => set('specialization', e.target.value)} placeholder={tch('ph_specialization')} className={inputCls} name="reg-specialization-staff" autoComplete="off" />
          </div>
          </>)}

          {/* Professional verification, specialization + KYC — Consultants only */}
          {isConsultant && (<>
          <div className="rounded-lg border border-gray-200 p-3 bg-gray-50/60">
            <p className={labelCls + ' mb-2'}>{tch('professional_verification')} <span className="text-[10px] font-normal text-gray-500">— LinkedIn URL or CV upload (either one)</span></p>
            <input type="url" value={form.linkedin_url} onChange={e => set('linkedin_url', e.target.value)} placeholder="https://linkedin.com/in/your-handle" className={inputCls} name="reg-linkedin" autoComplete="off" />
            {form.linkedin_url.trim() && (
              <p className={`mt-1 text-xs flex items-center gap-1 ${LINKEDIN_URL_RE.test(form.linkedin_url.trim()) ? 'text-green-700' : 'text-amber-600'}`}>
                {LINKEDIN_URL_RE.test(form.linkedin_url.trim())
                  ? <><CheckCircle2 className="w-3.5 h-3.5" /> Looks like a valid LinkedIn profile URL</>
                  : 'Not a recognized LinkedIn profile URL yet'}
              </p>
            )}
            <div className="mt-2 flex items-center gap-2 text-xs">
              <span className={cvFile ? 'text-green-700 flex items-center gap-1' : 'text-gray-400 flex items-center gap-1'}>
                {cvFile ? <><CheckCircle2 className="w-3.5 h-3.5" /> Verified CV: <span className="font-mono">{cvFile.name}</span></> : <><FileText className="w-3.5 h-3.5" /> No CV uploaded yet</>}
              </span>
              <button type="button" onClick={() => manualCvFileRef.current?.click()} disabled={cvParsing} className="text-blue-700 hover:underline font-medium shrink-0 disabled:opacity-50">
                {cvFile ? 'Change CV' : 'Upload CV'}
              </button>
              <input ref={manualCvFileRef} type="file" accept=".pdf,.docx" className="hidden" onChange={e => e.target.files?.[0] && handleCvUpload(e.target.files[0])} />
            </div>
            {cvNameMismatch && (
              <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                ⚠ This CV appears to belong to <strong>{cvNameMismatch}</strong> — please confirm this is your CV, or that your typed name matches.
              </p>
            )}
          </div>

          <div>
            <label className={labelCls}>{tch('specialization')}</label>
            <input type="text" value={form.specialization} onChange={e => set('specialization', e.target.value)} placeholder={tch('ph_specialization')} className={inputCls} name="reg-specialization" autoComplete="off" />
          </div>

          {/* KYC ID */}
          <div className="rounded-lg border border-gray-200 p-3 bg-gray-50/60 space-y-2.5">
            <p className={labelCls + ' mb-0'}>{tch('kyc')} <span className="text-[10px] font-normal text-gray-500">— upload your government ID; we read the number automatically</span></p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <select value={idType} onChange={e => setIdType(e.target.value)} className={inputCls}>
                {['Passport', 'National ID', 'Aadhaar', 'PAN', "Driver's License", 'Other'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input type="text" data-private value={idNumber} onChange={e => setIdNumber(e.target.value)} placeholder={tch('ph_id_autofilled')} className={inputCls} />
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => idFileRef.current?.click()} disabled={idParsing} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-blue-700 border border-blue-200 bg-white rounded-md hover:bg-blue-50 disabled:opacity-50">
                {idParsing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {idParsing ? 'Reading ID…' : (idFile ? 'Replace ID file' : 'Upload ID (image or PDF)')}
              </button>
              {idFile && !idParsing && <span className="text-xs text-green-700 flex items-center gap-1 min-w-0"><CheckCircle2 className="w-3.5 h-3.5 shrink-0" /><span className="truncate font-mono">{idFile.name}</span></span>}
              <input ref={idFileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*" className="hidden" onChange={e => e.target.files?.[0] && handleIdUpload(e.target.files[0])} />
            </div>
            {idError && <p className="text-xs text-amber-600">{idError}</p>}
            {idVerified === true && (
              <p className="text-xs text-emerald-700">
                ✓ Read on our own server and checked against the document's own verification
                number. Nothing was sent anywhere.
              </p>
            )}
            {idVerified === false && (
              <p className="text-xs text-amber-700">
                Read on our own server, so nothing was sent anywhere — but this document carries
                no verification number, so <b>please check the number above matches your card</b>.
              </p>
            )}
            <p className="text-[10px] text-gray-400">Your ID is stored securely and the number is encrypted. Visible only to you and Admin.</p>
          </div>
          </>)}

          {/* Profile pic + certs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg border border-gray-200 p-3 bg-gray-50/60">
              <p className={labelCls}>{tch('profile_picture')} <span className="text-[10px] font-normal text-gray-400">(optional)</span></p>
              <button type="button" onClick={() => profileRef.current?.click()} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-blue-700 border border-blue-200 bg-white rounded-md hover:bg-blue-50">
                <Upload className="w-3.5 h-3.5" /> {profileFile ? 'Replace photo' : 'Upload photo'}
              </button>
              {profileFile && <p className="text-xs text-green-700 mt-1.5 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /><span className="truncate">{profileFile.name}</span></p>}
              <input ref={profileRef} type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && setProfileFile(e.target.files[0])} />
            </div>
            <div className="rounded-lg border border-gray-200 p-3 bg-gray-50/60">
              <p className={labelCls}>{tch('certifications')} <span className="text-[10px] font-normal text-gray-400">(optional)</span></p>
              <button type="button" onClick={() => certRef.current?.click()} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-blue-700 border border-blue-200 bg-white rounded-md hover:bg-blue-50">
                <Upload className="w-3.5 h-3.5" /> {certFile ? 'Replace file' : 'Upload certifications'}
              </button>
              {certFile && <p className="text-xs text-green-700 mt-1.5 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /><span className="truncate">{certFile.name}</span></p>}
              <input ref={certRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.docx,image/*,application/pdf" className="hidden" onChange={e => e.target.files?.[0] && setCertFile(e.target.files[0])} />
            </div>
          </div>

          <button type="submit" disabled={submitting} className="w-full bg-linear-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 rounded-xl shadow-lg shadow-blue-500/25 disabled:opacity-50 transition-all">
            {submitting ? <span className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Creating account…</span> : 'Create my account'}
          </button>

          <p className="text-xs text-center text-gray-400 pt-1">
            Already have an account? <Link to="/login" className="text-blue-600 hover:underline">{tch('sign_in')}</Link>
          </p>
        </form>
      </div>
    </div>
  )
}
